import React, { useEffect, useMemo, useRef, useState } from "react";
import { injectIntl, intlShape } from "react-intl";
import { Checkbox, DialogBox, DialogBoxFooterType, Loader, Pane, SelectInput, Tabs } from "~/core";
import { messages } from "../i18n-messages";
import * as selectors from "../../selectors";
import { useAppDispatch, useAppSelector } from "~/store/hooks";

import { AgvanceType } from "../rec-list/components/rec-accordion/rec-accordion-item-context-menu";
import { IOption } from "~/core/components/select-input/model";

import { getAgvanceLocationsList } from "~/action-panel/components/field-module/selectors";
import { actions as fieldActions } from "~/action-panel/components/field-module/";
import {
    useLoginUserPreferences,
    useUpdateLoginUserPreferences,
} from "~/core/components/login-user-preferences";
import "./send-rec-agvance.css";
import { getTheUserPersonalityId } from "~/login/selectors";
import * as blending from "../../../common/product-blend-modal/blending-utils";
import { getProductBlendPicklists } from "~/action-panel/components/common/product-blend-modal/selectors";
import { AgvanceUtils } from "~/admin/setup/customer/agvance-utils";
import { RecDetails, RecSummary } from "~/recs-events/recs/model";
import { clearRecDetails, fetchNonMapRecDetails } from "~/recs-events/actions";
import { getRecDetails } from "~/recs-events/recs/selectors";
import { models } from "~/recs-events";
import classNames from "classnames";
import { AgvanceAPI, Ai360API } from "@ai360/core";
import { IPlanTicket, ILocationResponse, IRecProduct } from "@ai360/core/dist/4x/es/api/agvance";

interface ISendRecAgvanceProps {
    agvanceType: AgvanceType;
    isOpen: boolean;
    intl: intlShape;
    onSend: (evt: { workOrder: string; instructions: string }) => void;
    onClose: () => void;
    onApiError: (err, message) => void;
    recSummary: RecSummary;
    userGuid: string;
}

const SEND_RECS = "SendRecs";
const DROPDOWN_DATA = "dropdownData";

enum StatusMessage {
    REQUEST_SENT = "Sending Rec to Agvance...",
    SUCCESS = "{0} created : {1}", // 0 = Plan/Blend Ticket, 1 = blend ticket / plan letter
    ERROR = "Failed to send Rec.",
}
interface ITabInfo {
    prodMixId: string;
    prodMixName: string;
    products: IRecProduct[];
    include?: boolean;
}

const SendRecAgvance = (props: ISendRecAgvanceProps): JSX.Element => {
    const dispatch = useAppDispatch();

    const recGenerealDetails: RecDetails = useAppSelector(getRecDetails);
    const personalityId = useAppSelector(getTheUserPersonalityId);

    const productBlendPicklists = useAppSelector(getProductBlendPicklists);
    const locationList = useAppSelector(getAgvanceLocationsList) as ILocationResponse[];
    const isRecLoading = useAppSelector(selectors.getSendRecToLoading) as boolean;

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [disableSend, setDisableSend] = useState<boolean>(true);
    const blendSetType = useRef<string>(null);

    const [allProductsInTable, setAllProductsInTable] = useState<IRecProduct[]>([]);
    const [productMixProdList, setProductMixProdList] = useState<ITabInfo[]>([]);
    const [isSinglePlanOrTicket, setIsSinglePlanOrTicket] = useState<boolean>(false);
    const [agvanceCrop, setAgvanceCrop] = useState<string>(null);
    const [agvanceChemistry, setAgvanceChemistry] = useState<string>(null);

    const nutrientsIncludePreference = useRef<Record<string, boolean>>({});
    const customerGuid = useRef<string>(props.recSummary?.customerGuid);

    const [planTicketRequest, setPlanTicketRequest] = useState<IPlanTicket>({
        agvanceLocationId: null,
        agvanceCropChemistry: agvanceChemistry,
        agvanceCrop: agvanceCrop,
        customerGuid: customerGuid.current,
        fieldGuid: null,
        isSingle: isSinglePlanOrTicket,
        products: [],
        productSetDescription: null,
        quoteStatus: AgvanceAPI.PricingOptionsEnum.NOT_QUOTED,
        recGeneralGuid: null,
    } as IPlanTicket);

    const { data: productList, isLoading: productLoading } = AgvanceAPI.useGetProductListQuery(
        {
            customerGuid: props.recSummary.customerGuid,
            agvanceLocationId: planTicketRequest.agvanceLocationId,
        },
        { skip: !props.recSummary.customerGuid || !planTicketRequest.agvanceLocationId }
    );
    const { data: productSetsList, isLoading: productSetsLoading } =
        AgvanceAPI.useGetProductSetsQuery(
            {
                customerGuid: props.recSummary.customerGuid,
                agvanceLocationId: planTicketRequest.agvanceLocationId,
                ...(blendSetType.current != null && { blendSetType: blendSetType.current }),
            },
            { skip: !props.recSummary.customerGuid || !planTicketRequest.agvanceLocationId }
        );
    const { data: agvanceCrops, isLoading: agvanceCropsLoading } =
        AgvanceAPI.useGetAgvanceCropsQuery(
            {
                customerGuid: props.recSummary.customerGuid,
            },
            { skip: !props.recSummary.customerGuid }
        );
    const { data: agvanceCropChemistryList, isLoading: agvanceCropChemistryLoading } =
        AgvanceAPI.useGetAgvanceCropChemistryQuery(
            { customerGuid: props.recSummary.customerGuid },
            { skip: !props.recSummary.customerGuid }
        );

    const [selectedTab, setSelectedTab] = useState<number>();

    const productRefreshRequired = useRef<boolean>(false);
    const statusMessage = useRef<string>("");
    const isMultiProduct = useRef<boolean>(true);
    const isAgvancePlan = useRef<boolean>(false);
    const includePreferenceSet = useRef<boolean>(false);

    const pricingOptions: IOption[] = [
        {
            label: AgvanceAPI.PricingOptionsEnum.NOT_QUOTED,
            value: AgvanceAPI.PricingOptionsEnum.NOT_QUOTED,
        },
        {
            label: AgvanceAPI.PricingOptionsEnum.QUOTED,
            value: AgvanceAPI.PricingOptionsEnum.QUOTED,
        },
    ];
    if (isAgvancePlan.current) {
        pricingOptions.push({
            label: AgvanceAPI.PricingOptionsEnum.PRICE_OFFERED,
            value: AgvanceAPI.PricingOptionsEnum.PRICE_OFFERED,
        });
    }

    const { loginUserPreferences, isLoading: isLoginUserPreferencesLoading } =
        useLoginUserPreferences(SEND_RECS, [DROPDOWN_DATA]);

    const { savePreference } = useUpdateLoginUserPreferences(SEND_RECS);

    const [sendPlan, planState] = AgvanceAPI.useSendPlanToAgvanceMutation();
    const [sendBlend, blendState] = AgvanceAPI.useSendBlendTicketToAgvanceMutation();

    const { formatMessage } = props.intl;

    const generateOptions = (
        list: any[],
        labelKey: string,
        valueKey: string,
        setFunction = null
    ): IOption[] => {
        if (list && list.length > 0) {
            const options = list.map((item) => {
                return { label: item[labelKey], value: item[valueKey] };
            });
            if (setFunction) {
                setFunction(options);
            }
            return options;
        }
        return [];
    };

    const locationsOptions = useMemo(
        () => (locationList ? generateOptions(locationList, "name", "agvanceLocationId") : []),
        [locationList]
    );

    const productSetsOptions = useMemo(
        () =>
            productSetsList
                ? generateOptions(productSetsList, "setDescription", "setDescription")
                : [],
        [productSetsList]
    );

    const cropChemistryOptions = useMemo(
        () =>
            agvanceCropChemistryList
                ? generateOptions(agvanceCropChemistryList, "name", "uniqueId")
                : [],
        [agvanceCropChemistryList]
    );

    const cropOptions = useMemo(
        () => (agvanceCrops ? generateOptions(agvanceCrops, "description", "mpsGuid") : []),
        [agvanceCrops]
    );

    useEffect(() => {
        if (AgvanceUtils.isAgvanceConnected(personalityId) && props.recSummary) {
            customerGuid.current = props.recSummary.customerGuid;
            isAgvancePlan.current = props.agvanceType === AgvanceType.Plan;
            if (locationList.length === 0) {
                dispatch(
                    fieldActions.fetchAgvanceLocationsList({
                        customerGuid: props.recSummary.customerGuid,
                        agvanceFieldOrgLevelGuid: null,
                    })
                );
            }
        }
        if (props.recSummary?.recGeneralGuid) {
            dispatch(fetchNonMapRecDetails(props.recSummary.recGeneralGuid));
        }
        return () => {
            dispatch(clearRecDetails());
            dispatch(Ai360API.util.invalidateTags(["AgvanceRec", "LoginPreferences"]));
        };
    }, []);

    useEffect(() => {
        if (recGenerealDetails) {
            setPlanTicketRequest({
                ...planTicketRequest,
                customerGuid: recGenerealDetails.customerGuid,
                fieldGuid: recGenerealDetails.fieldGuid,
                recGeneralGuid: recGenerealDetails.recGeneralGuid,
            });

            const blendTypeList = new Set<string>();
            const isEquation =
                recGenerealDetails.recType === models.REC_TYPE_NAME_EQUATION_APPLICATION ||
                recGenerealDetails.recType === models.REC_TYPE_NAME_EQUATION_PLANTING;

            if (productMixProdList.length === 0) {
                const prodMixes: ITabInfo[] = [];
                const distinctProds: IRecProduct[] = [];
                if (isEquation) {
                    recGenerealDetails?.recAreaList.forEach((recArea, i) => {
                        const existingNutrientNames = prodMixes.map((pm) => pm.prodMixId);
                        recArea.recs[0].recNutrientList.map((pm, j) => {
                            blendTypeList.add(pm.recNutrientProductMix.physicalState);
                            if (!existingNutrientNames.includes(pm.recNutrientGuid)) {
                                const nutrientProducts = pm.recNutrientProductMix.products.map(
                                    (p) => {
                                        const costUnit = blending.getPriceUnit(p.costUnitGuid, {
                                            productBlendPicklists,
                                        });
                                        const product = getEmptyProduct();
                                        product.mappingProductName = p.productName;
                                        product.productGuid = p.productGuid;
                                        product.costUnitGuid = p.costUnitGuid;
                                        product.listPriceDisplay =
                                            p.cost && Number(p.cost)
                                                ? `${Number(p.cost).toFixed(2)} ${costUnit.label}`
                                                : null;
                                        product.listPrice = p.cost ? Number(p.cost) : null;
                                        product.productMixIndex = j + 1;
                                        return product;
                                    }
                                );
                                distinctProds.push(...nutrientProducts);
                                prodMixes.push({
                                    include: true,
                                    prodMixId: pm.recNutrientGuid,
                                    prodMixName: pm.nutrientName,
                                    products: nutrientProducts,
                                });
                            }
                        });
                    });
                } else {
                    recGenerealDetails?.recAreaList.forEach((recArea, i) => {
                        const existingProdMixIds = prodMixes
                            .filter(Boolean)
                            .map((pm) => pm.prodMixId);
                        recArea.recs[0].productMixList.forEach((pm, j) => {
                            pm.products.map((p) => blendTypeList.add(p.physicalState));
                            if (!existingProdMixIds.includes(pm.productMixId)) {
                                const products = pm.products.map((p) => {
                                    const costUnit = blending.getPriceUnit(p.costUnitGuid, {
                                        productBlendPicklists,
                                    });
                                    const product = getEmptyProduct();
                                    product.mappingProductName = p.productName;
                                    product.productGuid = p.productGuid;
                                    product.costUnitGuid = p.costUnitGuid;
                                    product.listPriceDisplay =
                                        p.cost && Number(p.cost)
                                            ? `${Number(p.cost).toFixed(2)} ${costUnit.label}`
                                            : null;
                                    product.listPrice = p.cost ? p.cost : null;
                                    product.productMixIndex = j + 1;
                                    return product;
                                });
                                distinctProds.push(...products);
                                prodMixes.push({
                                    include: true,
                                    prodMixId: pm.productMixId,
                                    prodMixName: pm.name,
                                    products: products,
                                });
                            }
                        });
                    });
                }
                isMultiProduct.current = prodMixes.filter(Boolean).length > 1;
                setIsSinglePlanOrTicket(isMultiProduct.current ? isSinglePlanOrTicket : true);
                blendSetType.current =
                    blendTypeList.size == 1 ? blendTypeList.values().next().value : null;
                setProductMixProdList(prodMixes);
                setAllProductsInTable(distinctProds);
            }
        }
    }, [recGenerealDetails]);

    useEffect(() => {
        setIsLoading(
            isRecLoading ||
                agvanceCropsLoading ||
                agvanceCropChemistryLoading ||
                productLoading ||
                productSetsLoading ||
                isLoginUserPreferencesLoading
        );
    }, [
        isRecLoading,
        agvanceCropsLoading,
        agvanceCropChemistryLoading,
        productLoading,
        productSetsLoading,
        isLoginUserPreferencesLoading,
    ]);

    useEffect(() => {
        if (loginUserPreferences?.dropdownData) {
            try {
                const dropdownData = JSON.parse(loginUserPreferences.dropdownData);
                setIsSinglePlanOrTicket(dropdownData.single);

                if (dropdownData.nutrientsInclude) {
                    nutrientsIncludePreference.current = dropdownData.nutrientsInclude;
                }
                setPlanTicketRequest({
                    ...planTicketRequest,
                    agvanceLocationId: dropdownData.location ?? dropdownData.location,
                    quoteStatus: dropdownData.pricing ?? dropdownData.pricing,
                    productSetDescription: dropdownData.productSet ?? dropdownData.productSet,
                } as unknown as IPlanTicket);
            } catch (error) {
                console.error("Error parsing dropDownData:", error);
            }
        }
    }, [isLoginUserPreferencesLoading, loginUserPreferences]);

    useEffect(() => {
        let noMatchedProducts = false;
        let missingAgvanceParams = true;

        missingAgvanceParams =
            planTicketRequest.agvanceLocationId === null ||
            planTicketRequest.productSetDescription === null;

        if (isSinglePlanOrTicket) {
            noMatchedProducts = allProductsInTable.some((p) => p.productId === null);
        } else {
            const productMixesToInclude = productMixProdList.filter((pm) => pm.include);
            if (productMixesToInclude.length > 0) {
                noMatchedProducts = productMixesToInclude.some((pm) =>
                    pm.products.some((p) => p.productId === null)
                );
            } else {
                noMatchedProducts = true;
            }
        }
        setDisableSend(noMatchedProducts || missingAgvanceParams);
    }, [
        planTicketRequest.agvanceLocationId,
        planTicketRequest.productSetDescription,
        allProductsInTable,
        productMixProdList,
        isSinglePlanOrTicket,
    ]);

    useEffect(() => {
        if (productMixProdList.length > 0 && !includePreferenceSet.current) {
            const prodMixList = [...productMixProdList];
            prodMixList.forEach((pm) => {
                if (nutrientsIncludePreference.current[pm.prodMixId]) {
                    pm.include = nutrientsIncludePreference.current[pm.prodMixId];
                } else {
                    pm.include = false;
                }
            });
            includePreferenceSet.current = true;
            setProductMixProdList([...prodMixList]);
        }
    }, [nutrientsIncludePreference, productMixProdList]);

    useEffect(() => {
        if (props.recSummary.cropGuids && props.recSummary.cropGuids.length > 0) {
            const matchedCrop = cropOptions.find((c) => c.value === props.recSummary.cropGuids[0]);
            setAgvanceCrop(matchedCrop ? matchedCrop.value.toString() : null);
        }
    }, [cropOptions]);

    useEffect(() => {
        // BUG FIX: Possible fix tried but did not work
        if (productRefreshRequired.current) {
            setProductMixProdList([...productMixProdList]);
            setAllProductsInTable([...allProductsInTable]);
            productRefreshRequired.current = false;
        }
    }, [productRefreshRequired]);

    const getEmptyProduct = (): IRecProduct => {
        return {
            agvancePhysicalState: null,
            billingUOM: null,
            billingUomMappingGuid: null,
            blendUOM: null,
            blendUnitMappingGuid: null,
            costUnitGuid: null,
            departmentId: null,
            inventoryUnit: null,
            listPrice: null,
            listPriceDisplay: null,
            mappingGuid: null,
            mappingProductName: null,
            productMixIndex: 0,
            productGuid: null,
            packageUnit: null,
            productId: null,
            ratingUOM: null,
            ratingUomMappingGuid: null,
        } as unknown as IRecProduct;
    };

    const handleSavePreference = async () => {
        const dropdownData = {
            location: planTicketRequest.agvanceLocationId,
            productSet: planTicketRequest.productSetDescription,
            pricing: planTicketRequest.quoteStatus,
            single: isSinglePlanOrTicket,
            nutrientsInclude: productMixProdList.reduce<Record<string, boolean>>((acc, item) => {
                acc[item.prodMixId] = item.include;
                return acc;
            }, {}),
        };
        await savePreference(DROPDOWN_DATA, JSON.stringify(dropdownData));
    };

    const updateStatusMessage = (latestMessage: string) => {
        statusMessage.current = statusMessage.current + `\n${latestMessage}`;
    };

    const formatString = (template: string, ...args: any[]): string => {
        return template.replace(/{(\d+)}/g, (match, index) => {
            return typeof args[index] !== "undefined" ? args[index] : match;
        });
    };

    const sendRecToAgvance = async (requestList: IPlanTicket[]) => {
        if (!requestList.length) return;
        try {
            const response = isAgvancePlan.current
                ? await sendPlan(requestList).unwrap()
                : await sendBlend(requestList).unwrap();
            if (response) {
                // TO DO : IOT message with ticket or blend
                updateStatusMessage(
                    formatString(
                        StatusMessage.SUCCESS,
                        isAgvancePlan.current ? "Plan Letter:" : "Blend Ticket",
                        "ABC"
                    )
                );
                setIsLoading(false);
            }
        } catch (err) {
            updateStatusMessage(StatusMessage.ERROR);
            updateStatusMessage(`${err.status}:  ${err.data.errors.map((e) => `${e.message}\n`)}`);
            setDisableSend(false);
            setIsLoading(false);
        }
    };

    const onWorkPlanSend = () => {
        setIsLoading(true);
        setDisableSend(true);
        // Resets the status message for each click. Can have it rolling.
        statusMessage.current = StatusMessage.REQUEST_SENT;

        const requests = [];
        handleSavePreference();
        planTicketRequest.isSingle = isSinglePlanOrTicket;
        if (isSinglePlanOrTicket) {
            // Call with single plan/ticket products
            planTicketRequest.products = allProductsInTable;
            if (planTicketRequest.quoteStatus === AgvanceAPI.PricingOptionsEnum.NOT_QUOTED) {
                allProductsInTable.forEach((p) => (p.listPrice = null));
            }
            requests.push(planTicketRequest);
        } else {
            // Call with product mix products
            requests.push(
                ...productMixProdList
                    .filter((pm) => pm.include)
                    .map((pm) => {
                        if (
                            planTicketRequest.quoteStatus ===
                            AgvanceAPI.PricingOptionsEnum.NOT_QUOTED
                        ) {
                            pm.products.forEach((p) => (p.listPrice = null));
                        }
                        const request: IPlanTicket = JSON.parse(JSON.stringify(planTicketRequest));
                        request.products = pm.products;
                        request.productMixId = pm.prodMixId;
                        request.productMixName = pm.prodMixName;
                        return request;
                    })
                    .filter(Boolean)
            );
        }
        sendRecToAgvance(requests);
    };

    const onProductChange = (value, index) => {
        let updateProductRow: IRecProduct = null;
        const productDetails = productList.find((p) => p.prodId === value);
        if (isSinglePlanOrTicket) {
            // Update in allProducts
            updateProductRow = allProductsInTable[index];
        } else {
            // Update in productMix
            const updateProductMix = productMixProdList[selectedTab];
            updateProductRow = updateProductMix
                ? (updateProductMix.products[index] as IRecProduct)
                : null;
        }
        if (updateProductRow) {
            updateProductRow.agvancePhysicalState = productDetails.dryOrLiquid;
            updateProductRow.billingUomMappingGuid = productDetails.billingUnitMappingGuid;
            updateProductRow.billingUOM = productDetails.billingUnit;
            updateProductRow.blendUOM = productDetails.blendUnit;
            updateProductRow.blendUnitMappingGuid = productDetails.blendUnitMappingGuid;
            updateProductRow.departmentId = productDetails.deptId;
            updateProductRow.inventoryUnit = productDetails.inventoryUnit;
            updateProductRow.mappingGuid = productDetails.mappingGuid;
            updateProductRow.packageUnit = productDetails.packageUnit;
            updateProductRow.productId = productDetails.prodId;
            updateProductRow.ratingUOM = productDetails.rateUnit;
            updateProductRow.ratingUomMappingGuid = productDetails.rateUnitMappingGuid;
        }
        setAllProductsInTable([...allProductsInTable]);
        setProductMixProdList([...productMixProdList]);
    };

    const getHeader = () => {
        const showPricing =
            planTicketRequest.quoteStatus !== AgvanceAPI.PricingOptionsEnum.NOT_QUOTED;
        return (
            <div className="product-header" key="header">
                {isSinglePlanOrTicket && isMultiProduct.current && (
                    <div className="product-column xsmall">{formatMessage(messages.mixId)}</div>
                )}
                <div className="product-column xxlarge">{formatMessage(messages.products)}</div>
                <div className="product-column agvance-products xxxlarge">
                    {formatMessage(messages.agvanceProducts)}
                </div>
                <div className="product-column xsmall">{formatMessage(messages.deptId)}</div>
                <div className="product-column xsmall">{formatMessage(messages.invUnit)}</div>
                <div className="product-column xsmall">{formatMessage(messages.pkgUnit)}</div>
                <div className="product-column xsmall">{formatMessage(messages.bldUnit)}</div>
                {showPricing && (
                    <div className={classNames("product-column", "xsmall")}>
                        {formatMessage(messages.pricing)}
                    </div>
                )}
            </div>
        );
    };

    const generateRows = (products: IRecProduct[], key: any, mixIncluded?: boolean) => {
        const showPricing =
            planTicketRequest.quoteStatus !== AgvanceAPI.PricingOptionsEnum.NOT_QUOTED;
        let localFlag = false;
        const rows = products.map((product, i) => {
            const options = generateOptions(
                productList?.filter((p) => p.mappingGuid === product.productGuid),
                "name",
                "prodId"
            );
            const productDetails = productList?.find((p) => p.mappingGuid === product.productGuid);
            // TO DO: make this better
            if (options.length === 1 && product.productId === null) {
                product.billingUomMappingGuid = productDetails.billingUnitMappingGuid;
                product.billingUOM = productDetails.billingUnit;
                product.blendUOM = productDetails.blendUnit;
                product.blendUnitMappingGuid = productDetails.blendUnitMappingGuid;
                product.departmentId = productDetails.deptId;
                product.inventoryUnit = productDetails.inventoryUnit;
                product.mappingGuid = productDetails.mappingGuid;
                product.packageUnit = productDetails.packageUnit;
                product.productId = productDetails.prodId;
                product.ratingUOM = productDetails.rateUnit;
                product.ratingUomMappingGuid = productDetails.rateUnitMappingGuid;
                localFlag = mixIncluded;
            }
            return (
                <div className="product-row" key={`product-combined-${key}-${i}`}>
                    {isSinglePlanOrTicket && isMultiProduct.current && (
                        <div className="product-column xsmall">{product.productMixIndex}</div>
                    )}
                    <div className="product-column xxlarge">{product.mappingProductName}</div>
                    <div className="product-column agvance-products xxxlarge">
                        <SelectInput
                            disabled={isLoading}
                            reactKey={`select-input-${key}-${i}`}
                            initialFilterStr={null}
                            value={product.productId || (options.length === 1 && options[0].value)}
                            clearable={false}
                            options={options}
                            onChange={(value) => onProductChange(value, i)}
                            showTopLabel={false}
                        />
                    </div>
                    <div className={classNames("product-column", "xsmall")}>
                        {product.departmentId}
                    </div>
                    <div className={classNames("product-column", "xsmall")}>
                        {product.inventoryUnit}
                    </div>
                    <div className={classNames("product-column", "xsmall")}>
                        {product.packageUnit}
                    </div>
                    <div className={classNames("product-column", "xsmall")}>{product.blendUOM}</div>
                    {showPricing && (
                        <div className={classNames("product-column", "xsmall")}>
                            {product.listPriceDisplay}
                        </div>
                    )}
                </div>
            );
        });
        productRefreshRequired.current = localFlag;
        return rows;
    };

    const updateNutrientIncludePreference = (mixId: string, value: boolean) => {
        productMixProdList.find((pm) => pm.prodMixId === mixId).include = value;
        setProductMixProdList([...productMixProdList]);
    };

    const getPanes = () => {
        const panes = productMixProdList.map((pm, i) => {
            return (
                <Pane key={`${pm.prodMixName}-${i}`} label={pm.prodMixName}>
                    {
                        <Checkbox
                            key={`checkbox-${pm.prodMixName}-${i}`}
                            disabled={isLoading}
                            label={formatMessage(messages.include)}
                            onChange={(e, value) =>
                                updateNutrientIncludePreference(pm.prodMixId, value)
                            }
                            value={pm.include}
                        />
                    }
                    <div className="product-table">
                        {[getHeader(), ...generateRows(pm.products, i, pm.include)]}
                    </div>
                </Pane>
            );
        });
        return [...panes];
    };

    const getTabbedProductList = () => {
        return (
            <Tabs
                key={"product-mix-tab"}
                className="blend-type-tabs"
                selected={selectedTab}
                onTabSelect={(idx) => setSelectedTab(idx)}
            >
                {getPanes()}
            </Tabs>
        );
    };

    const getCombinedProductList = () => {
        const productRows = generateRows(allProductsInTable, "combined");
        return (
            <div className="product-table">
                {getHeader()}
                {productRows}
            </div>
        );
    };

    const updateLocation = (value) => {
        // Clear products set in all mixes
        allProductsInTable.forEach((p) => {
            p.agvancePhysicalState = null;
            p.billingUomMappingGuid = null;
            p.billingUOM = null;
            p.blendUOM = null;
            p.blendUnitMappingGuid = null;
            p.departmentId = null;
            p.inventoryUnit = null;
            p.mappingGuid = null;
            p.packageUnit = null;
            p.productId = null;
            p.ratingUOM = null;
            p.ratingUomMappingGuid = null;
        });
        productMixProdList.forEach((pm) => {
            pm.products.forEach((p) => {
                p.agvancePhysicalState = null;
                p.billingUomMappingGuid = null;
                p.billingUOM = null;
                p.blendUOM = null;
                p.blendUnitMappingGuid = null;
                p.departmentId = null;
                p.inventoryUnit = null;
                p.mappingGuid = null;
                p.packageUnit = null;
                p.productId = null;
                p.ratingUOM = null;
                p.ratingUomMappingGuid = null;
            });
        });
        setProductMixProdList([...productMixProdList]);
        setAllProductsInTable([...allProductsInTable]);
        setPlanTicketRequest({
            ...planTicketRequest,
            agvanceLocationId: value,
            productSetDescription: null,
        });
    };

    const updatePlanOrBlend = (key, value) => {
        planTicketRequest[key] = value;
        setPlanTicketRequest({ ...planTicketRequest });
    };

    const updatePlanOrBlendFromList = (
        key,
        value,
        optionsList,
        listKey,
        valueToSet,
        setState = null
    ) => {
        // To set the label insted of value to ticket.
        // For Crop and Chemistry, not sure if Name will be unique, so using another property. API needs Name.
        const optionDetails = optionsList.find((option) => option[listKey] === value);
        const newPlanOrTicket = JSON.parse(JSON.stringify(planTicketRequest)) as IPlanTicket;
        newPlanOrTicket[key] = optionDetails ? optionDetails[valueToSet] : null;
        setPlanTicketRequest(newPlanOrTicket);
        if (setState) {
            setState(value);
        }
    };

    return (
        <DialogBox
            action="Send"
            actionDisabled={disableSend || isLoading}
            className={"send-rec-agvance-dialog"}
            draggable
            footerType={DialogBoxFooterType.ACTION_CLOSE}
            forceOverflow
            isOpen={true}
            onAction={onWorkPlanSend}
            onClose={props.onClose}
            title={
                isAgvancePlan.current
                    ? formatMessage(messages.sendRecToAgvancePlanTitle)
                    : formatMessage(messages.sendRecToAgvanceBlendTicketTitle)
            }
            unrestricted
        >
            {isLoading && <Loader />}
            <div className="property-bar">
                <SelectInput
                    required
                    clearable={false}
                    disabled={isLoading}
                    placeholderText={formatMessage(messages.location)}
                    options={locationsOptions}
                    onChange={(value) => updateLocation(value)}
                    value={planTicketRequest.agvanceLocationId}
                />
                <SelectInput
                    required
                    disabled={isLoading}
                    clearable={false}
                    placeholderText={formatMessage(messages.productSet)}
                    options={productSetsOptions}
                    onChange={(value) => updatePlanOrBlend("productSetDescription", value)}
                    value={planTicketRequest.productSetDescription}
                />
                <SelectInput
                    required
                    disabled={isLoading}
                    clearable={false}
                    placeholderText={formatMessage(messages.pricing)}
                    options={pricingOptions}
                    onChange={(value) => updatePlanOrBlend("quoteStatus", value)}
                    value={planTicketRequest.quoteStatus}
                />
                <Checkbox
                    disabled={isLoading || !isMultiProduct.current}
                    label={
                        isAgvancePlan.current
                            ? formatMessage(messages.singlePlan)
                            : formatMessage(messages.singleBlendTicket)
                    }
                    onChange={(e, value) => setIsSinglePlanOrTicket(value)}
                    value={isSinglePlanOrTicket}
                />
            </div>
            <div className="agvance-select-input-container">
                <SelectInput
                    disabled={isLoading}
                    required={false}
                    options={cropOptions}
                    onChange={(value) =>
                        updatePlanOrBlendFromList(
                            "agvanceCrop",
                            value,
                            agvanceCrops,
                            "mpsGuid",
                            "description",
                            setAgvanceCrop
                        )
                    }
                    placeholderText={formatMessage(messages.agvanceCrop)}
                    value={agvanceCrop}
                />
                <SelectInput
                    disabled={isLoading}
                    required={false}
                    placeholderText={formatMessage(messages.agvanceCropChemistry)}
                    options={cropChemistryOptions}
                    onChange={(value) =>
                        updatePlanOrBlendFromList(
                            "agvanceCropChemistry",
                            value,
                            agvanceCropChemistryList,
                            "uniqueId",
                            "name",
                            setAgvanceChemistry
                        )
                    }
                    value={agvanceChemistry}
                />
            </div>
            <div className="product-table-container">
                {isSinglePlanOrTicket ? getCombinedProductList() : getTabbedProductList()}
            </div>
            <div className="status-message">
                {(planState.isLoading || blendState.isLoading || statusMessage.current) && (
                    <div className="message-box">{`${statusMessage.current}`}</div>
                )}
            </div>
        </DialogBox>
    );
};

export default injectIntl(SendRecAgvance);
